import React, { FC, FormEvent, useEffect, useState } from 'react';
import { from, Subscription } from 'rxjs';
import jsCookie from 'js-cookie';

// eslint-disable-next-line camelcase
import Alert, { defaultStrings_en_US } from '@glu/alerts-react';
import { Button, PRIMARY } from '@glu/buttons-react';
import { Input } from '@glu/form-components';
import gluLocale from '@glu/locale';
import { useValidation } from '@glu/validation-react';

import { useAppConfig } from 'components/appConfig/AppConfig';
import useLocale from 'components/useLocale';
import { unsubscribeComponent } from 'components/utils/util';
import { HTTP_METHODS, ENVIRONMENTS } from 'constants.js';

import useStyles from './SSOLogin.styles';

gluLocale.set(defaultStrings_en_US);

const { DEV, QA1, QA2, QA3, QA4 } = ENVIRONMENTS;

const subscriptions: Record<string, null | Subscription> = {
  login: null
};

const getClientSecret = (env: string) => {
  switch (env) {
    case DEV:
      return 'b6b4afae-3a45-48b8-8b03-b77fb925e856';
    case QA1:
    case QA2:
    case QA3:
    case QA4:
      return '6f78b738-8ca9-4286-a88f-e9318d1a4aa2';
    default:
      return '';
  }
};

const SSOLogin: FC = () => {
  const locale = useLocale();
  const { article, articleContainer, login, loginButton, loginForm } = useStyles();
  const [errorMessage, setErrorMessage] = useState('');
  const { env } = useAppConfig();
  const [FormContextProvider, formContext, { errors, validate }] =
    useValidation<Record<'customerCode' | 'userCode', string>>();

  useEffect(() => () => unsubscribeComponent(subscriptions), []);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();

    validate().then(({ data: { customerCode, userCode }, isValid }) => {
      if (isValid) {
        subscriptions.login = from(
          fetch('/dbiqp/keys/premier/inboundsso', {
            method: HTTP_METHODS.POST,
            body: new URLSearchParams({
              client_id: 'inboundsso-vast-bank',
              client_secret: getClientSecret(env),
              user_code: userCode,
              customer_code: customerCode
            }),
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }).then((res) => res.json())
        ).subscribe(
          // eslint-disable-next-line camelcase
          ({ cookieName, cookieValue, error }) => {
            if (error) {
              setErrorMessage(error);
            } else {
              jsCookie.set(cookieName, cookieValue);
              window.location.replace('/');
            }
          },
          () => {
            setErrorMessage(locale['common.server.error']);
          }
        );
      }
    });
  };

  const onAlertHide = () => {
    setErrorMessage('');
  };

  return (
    <main>
      <div className={articleContainer}>
        <article className={article}>
          <section className={login}>
            <FormContextProvider value={formContext}>
              <form
                className={loginForm}
                data-qa="ssoLoginForm"
                name="ssoLoginForm"
                onSubmit={onSubmit}
              >
                {errorMessage && (
                  <Alert
                    dataQa="ssoLoginAlert"
                    dismissible
                    message={errorMessage}
                    onHide={onAlertHide}
                    variant="error"
                  />
                )}
                <fieldset>
                  <div data-qa="customerCodeContainer">
                    <Input
                      data-qa="customerCode"
                      htmlId="customerCode"
                      labelText={locale['logon.prompt.sso.usergroup']}
                      name="customerCode"
                      validators={[{ required: true, msg: locale['validation.required'] }]}
                    />
                  </div>
                  <div data-qa="userCodeContainer">
                    <Input
                      data-qa="userCode"
                      htmlId="userCode"
                      labelText={locale['logon.prompt.sso.userCode']}
                      name="userCode"
                      validators={[{ required: true, msg: locale['validation.required'] }]}
                    />
                  </div>
                  <Button
                    className={loginButton}
                    dataQa="ssoLoginButton"
                    disabled={!!(errors?.customerCode?.length || errors?.userCode?.length)}
                    text={locale['button.signIn']}
                    type="submit"
                    variant={PRIMARY}
                  />
                </fieldset>
              </form>
            </FormContextProvider>
          </section>
        </article>
      </div>
    </main>
  );
};

export default SSOLogin;
